import Controller from "./class.Controller.js";
import TransporterView from "./class.TransporterView.js";
import TransporterRunner from "./class.TransporterRunner.js";
import * as TransporterUtils from "./class.TransporterUtils.js";
import TransporterInput from "./class.TransporterInput.js";
import TransporterDebug from "./class.TransporterDebug.js";
import TransporterStorage from "./class.TransporterStorage.js";
import TransporterAnimations from "./class.TransporterAnimations.js";
import TransporterLog from "./class.TransporterLog.js";
import TransporterVariables from "./class.TransporterVariables.js";
import TransporterAnalytics from "./Analytics/class.TransporterAnalytics.js";
import Colors from "./colors";

const LOGGER = new TransporterLog({
	attributes: {
		name: "Transporter",
		color: "gold",
	},
});

let isMathJaxConfigured = false;
let numMathJaxConfigurationAttempts = 0;

/**
 * The Transporter controller.
 * TODO: Documentation
 */
export default class Transporter extends Controller {
	/**
	 * Initializes Transporter.
	 * Executes when data types have been loaded.
	 *
	 * @param {object} options
	 * @param {object} options.attributes.progress
	 * 	A progress object.
	 * @param {object} options.attributes.document
	 * 	A document object.
	 * @param {number} options.attributes.version
	 * 	The version number of the document.
	 * @param {(transporter) => void} options.attributes.onInitialize
	 * 	What to do when Transporter has initialized.
	 * @param {(progress) => void} options.attributes.onProgress
	 * 	What to do when the user makes progress.
	 * @param {boolean} options.attributes.enableCmty
	 * 	Whether to enable community icons in the view.
	 * @param {() => void} options.attributes.onCmtyStartTopic
	 * 	What to do when the user clicks the Start Topic icon.
	 * @param {() => void} options.attributes.onCmtyOpenDiscussions
	 * 	What to do when the user clicks the Open Discussions icon.
	 * @param {(resetCallback) => void} options.attributes.onStartOver
	 * 	What to do when the user chooses to Start Over. The reset callback
	 * 	accepts an object, where each key of the object is an attribute to set
	 * 	on the Transporter object. This allows you to change the document object
	 * 	during a reset, for example, by providing an object with the document key
	 * 	set to a new document object.
	 * @param {boolean} isPreviewMode
	 * 	Will be true when the transporter is instantiated in the editor.
	 *  Motor verification errors and parser index matches will be shown when this flag is true.
	 * @param {boolean} disableProgressModal
	 * @param {boolean} enableAnalytics
	 * @param {number} class_id
	 * @param {number} collection_id
	 * @param {number} user_id
	 * @param {number} playthrough_id
	 * @param {object} playthrough_seen
	 * @param {boolean} version_override
	 * 	Whether the user was forced to a newer version than their original progress.
	 * @param {boolean} demoMode
	 * @param {boolean} enableDevswag
	 */
	constructor(options) {
		super(options);

		// Initialize default values.
		this.document = _.isArray(this.document) ? this.document : [];
		this.progress = _.isObject(this.progress) ? this.progress : null;
		if (!_.isBoolean(this.isPreviewMode)) {
			this.isPreviewMode = false;
		}

		// Iniitalize transporterVariables
		this.transporterVariables = new TransporterVariables(options, this);

		/**
		 * Build the Analytics controller, responsible for tracking stats that
		 * help us improve the quality of scripts.
		 */
		this.analyticsController = new TransporterAnalytics({
			attributes: {
				mainController: this,
				collection_id: parseInt(this.collection_id) || null,
				document_id: parseInt(this.document_id) || null,
				version: parseInt(this.version) || null,
				user_id: parseInt(this.user_id) || null,
				class_id: parseInt(this.class_id) || null,
				playthrough_id: parseInt(this.playthrough_id) || null,
				playthrough_seen: this.playthrough_seen || null,
			},
		});
		// Make analytics reporting a part of onProgress and onComplete.
		const onProgress = this.onProgress;
		if (this.enableAnalytics) {
			const debouncedReport = _.debounce(() => {
				this.analyticsController.reporter.report();
			}, 2000);
			this.onProgress = (...n) => {
				onProgress(...n);
				debouncedReport();
			};
		}

		/**
		 * Build the storage controller, which is responsible for saving our
		 * client-side settings, currently into localStorage.
		 */
		this.storageController = new TransporterStorage({
			attributes: {
				mainController: this,
			},
		});

		/**
		 * Build the animation controller, which handles whether animations
		 * are enabled or disabled in this instance.
		 */
		this.animationController = new TransporterAnimations({
			attributes: {
				mainController: this,
			},
		});

		/**
		 * Build the main view. This is the main Transporter window.
		 */
		this.mainView = new TransporterView({
			attributes: {
				className: "aops-transporter-wrapper",
				mainController: this,
				progress: this.progress,
				enableCmty: this.enableCmty || false,
			},
		});

		/**
		 * Alias for the tipsController... it gets built by the
		 * TransporterView since it has a view component.
		 */
		this.tipsController = this.mainView.tipsView;

		/**
		 * Build the layout view. This is where chat bubbles are organized.
		 */
		this.layoutView = this.mainView.layoutView;

		/**
		 * Build the input controller. This is responsible for tracking user inputs,
		 * and restoring script state from a buffer of inputs.
		 */
		this.inputController = new TransporterInput({
			attributes: {
				mainController: this,
				progress: this.progress,
				disableProgressModal: this.disableProgressModal || false,
			},
		});

		// We have some custom settings for MathJax, so let's define them here.
		if ("MathJax" in window) {
			this._configureMathJax();
		}

		// When information about composite document types is loaded from z_data,
		// we can initialize the rest of Transporter.
		TransporterUtils.ExecuteWhenTypesLoaded(() => {
			this._createScrollbar();
			this.onInitialize(this);
			this.transporter();
		});
	}

	onInitialize() {}

	onProgress() {}

	async onComplete($anchor) {
		var progress = this.inputController.save();
		progress.isComplete = true;
		try {
			await this.analyticsController.getPlaythrough().setCompleted();
		} catch (e) {
			LOGGER.error(e);
		}
		this.onProgress(progress);
		LOGGER.log("The script has been completed.");
	}

	/**
	 * Starts the Runner.
	 */
	async transporter() {
		if (this.demoMode) {
			await this.mainView.showPlayButtonAndWait();
		}

		if (this.version_override) {
			await TransporterUtils.asyncPrompt(
				"We fixed a bug in this lesson after you started it, so you'll have to start again from the beginning. We're sorry for any inconvenience!",
				["OK"]
			);
		}

		/**
		 * Build the runner controller. This is responsible for traversing the document,
		 * and executing actions as described by z_data.
		 */
		this.runnerController = new TransporterRunner({
			document: this.document,
			attributes: {
				mainController: this,
				progress: this.progress,
				mainView: this.mainView,
				layoutView: this.layoutView,
				inputController: this.inputController,
			},
		});

		/**
		 * Builds the debug menu. A simple interface which lets you adjust minor things.
		 */
		this.debugger = new TransporterDebug({
			attributes: {
				className: "aops-transporter-debug",
				tagName: "div",
				mainController: this,
				enableDevswag: this.enableDevswag,
			},
		});

		// Check if the user wants to reset their progress.
		this.inputController.checkResumePrompt();

		// Start the script..
		this.runnerController.runByPointer({
			pointer: [],
			action: null,
			onAdvance: _.bind(this.onComplete, this),
			onBypass: () => {},
		});
	}

	async reset() {
		await this.analyticsController.getPlaythrough().setStartedOver();
		this.analyticsController.reporter.report();

		// Reset variables
		this.transporterVariables.resetVariables();

		const resetCallback = (newAttributes) => {
			if (newAttributes) {
				for (let key in newAttributes) {
					this[key] = newAttributes[key];
				}
			}
			// Retart the script..
			this.restartScript();
		};

		if (this.onStartOver) {
			this.onStartOver(resetCallback);
		} else {
			resetCallback();
		}
	}

	/**
	 * Start the lesson
	 */
	restartScript() {
		// Clear out the old progress
		this.progress = null;
		this.runnerController.progress = null;
		this.inputController.progress = null;

		this.inputController.reset(true);
		var progress = this.inputController.save();
		this.onProgress(progress);

		this.analyticsController.playthrough_id = this.playthrough_id;
		this.analyticsController.playthrough_seen = this.playthrough_seen;
		this.analyticsController.version = this.version;
		this.analyticsController.reset();
		this.animationController.reset();
		this.runnerController.reset();
		this.mainView.reset();

		// Reset variables
		this.transporterVariables.resetVariables();

		this.runnerController.runByPointer({
			pointer: [],
			action: null,
			onAdvance: _.bind(this.onComplete, this),
			onBypass: () => {},
		});
	}

	/**
	 * Call this function if you'd like to replace the browser default scrollbar
	 * with the AoPS scrollbar.
	 */
	_createScrollbar() {
		const $outer = $(`<div />`)
			.css({
				position: "relative",
				width: "100%",
				height: "100%",
				"overflow-x": "hidden",
				"overflow-y": "hidden",
				transform: "translate3d(0, 0, 0)",
			})
			.appendTo(this.mainView.$el);

		const $bar = $(`<div class="aops-scroll-bar"/>`)
			.css({position: "absolute"})
			.appendTo($outer);

		// eslint-disable-next-line no-unused-vars
		const $slider = $(`<div class="aops-scroll-slider" />`)
			.css({position: "relative"})
			.appendTo($bar);

		const $inner = this.mainView.$main
			.css({
				position: "relative",
				"overflow-y": "scroll",
				width: "calc(100% + 20px)",
				height: "100%",
			})
			.appendTo($outer);

		const $content = this.mainView.$layoutContainer
			.css({
				position: "relative",
				width: "calc(100% - 5px)",
			})
			.appendTo($inner);

		AoPS.Ui.buildAopsScrollbar({
			$scrollbar: $bar,
			$content,
			show_on_hover: true,
			fade_start_class: "aops-scroll-fade-top aops-scroll-faders",
			fade_end_class: "aops-scroll-fade-bottom aops-scroll-faders",
		});
	}

	/**
	 * Configures MathJax.
	 */
	_configureMathJax() {
		numMathJaxConfigurationAttempts++;
		if (!isMathJaxConfigured) {
			LOGGER.log("Setting MathJax renderer to CommonHTML.");
			MathJax.Hub.Queue(["setRenderer", MathJax.Hub, "CommonHTML"]);

			if ("Extension" in MathJax && "TeX/color" in MathJax.Extension) {
				LOGGER.log("Defining MathJax colors.");
				for (let key in Colors) {
					MathJax.Extension["TeX/color"].colors[key] = Colors[key]["css-hex"];
				}
			} else {
				// Sometimes it can take a while for the color extension to load, but it
				// does eventually load. Let's try a few times before we throw an error.
				if (numMathJaxConfigurationAttempts < 10) {
					var self = this;
					setTimeout(() => {
						self._configureMathJax();
					}, 1000);
				} else {
					LOGGER.error("MathJax extension TeX/color wasn't found.");
				}
			}

			isMathJaxConfigured = true;
		}
	}
}
